import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2,
  RotateCcw,
  ListTodo,
  ArrowLeft,
  Code2,
  Wrench,
  BarChart3,
  Anvil,
} from 'lucide-react';
import { useTryItStore } from '../stores/tryItStore';
import ActivityBar from '../components/try-it/ActivityBar';
import FileExplorer from '../components/try-it/FileExplorer';
import EditorPanel from '../components/try-it/EditorPanel';
import TerminalPanel from '../components/try-it/TerminalPanel';
import AssignmentCards from '../components/try-it/AssignmentCards';
import type { Assignment } from '../lib/tryit-types';
import type { ProjectOption } from '../lib/tryit-types';

const projectIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'greenfield-saas': Code2,
  'legacy-modernization': Wrench,
  'analytics-dashboard': BarChart3,
};

const projectGradients: Record<string, string> = {
  'greenfield-saas': 'from-blue-500/20 to-cyan-500/20',
  'legacy-modernization': 'from-amber-500/20 to-orange-500/20',
  'analytics-dashboard': 'from-purple-500/20 to-pink-500/20',
};

const projectBorders: Record<string, string> = {
  'greenfield-saas': 'border-blue-500/30 hover:border-blue-500/60',
  'legacy-modernization': 'border-amber-500/30 hover:border-amber-500/60',
  'analytics-dashboard': 'border-purple-500/30 hover:border-purple-500/60',
};

// ---------------------------------------------------------------------------
// Project Selector Screen
// ---------------------------------------------------------------------------

function ProjectSelector({
  projects,
  isLoading,
  error,
  onSelect,
  onRetry,
}: {
  projects: ProjectOption[];
  isLoading: boolean;
  error: string | null;
  onSelect: (id: string) => void;
  onRetry: () => void;
}) {
  if (isLoading && projects.length === 0) {
    return (
      <div className="h-screen w-screen bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 text-accent animate-spin" />
          <p className="text-sm text-gray-400">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error && projects.length === 0) {
    return (
      <div className="h-screen w-screen bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={onRetry} className="btn-primary text-sm py-2 px-4">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-dark-bg flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
        >
          <Anvil className="h-6 w-6 text-accent transition-transform duration-300 group-hover:scale-110" />
          <span className="text-lg font-bold tracking-tight text-white">
            Code<span className="text-accent">Forge</span>
          </span>
        </Link>
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to site
        </Link>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-4xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl font-bold text-white mb-3">
              Try <span className="text-gradient">Claude Code</span> in Action
            </h1>
            <p className="text-gray-400 max-w-xl mx-auto">
              Choose a project to explore. Each workspace comes with realistic
              source code, Jira tickets, GitHub PRs, and a CodeForge playbook —
              all powered by real Claude via AWS Bedrock.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {projects.map((project, i) => {
              const Icon = projectIcons[project.id] || Code2;
              return (
                <motion.button
                  key={project.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => onSelect(project.id)}
                  disabled={isLoading}
                  className={`text-left p-6 rounded-2xl border bg-surface-DEFAULT/60 backdrop-blur transition-all duration-300 hover:bg-surface-100/50 hover:shadow-xl hover:shadow-black/20 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed ${projectBorders[project.id] || 'border-dark-border'}`}
                >
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${projectGradients[project.id] || 'from-gray-500/20 to-gray-600/20'} mb-4`}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-1">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-surface-100 text-gray-300">
                      {project.language}
                    </span>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-surface-100 text-gray-300">
                      {project.framework}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500">{project.phase}</div>
                </motion.button>
              );
            })}
          </div>

          {isLoading && (
            <div className="flex items-center justify-center mt-8 gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating workspace...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace Screen (VS Code layout)
// ---------------------------------------------------------------------------

function WorkspaceScreen() {
  const store = useTryItStore();
  const [activePanel, setActivePanel] = useState<'explorer' | 'none'>(
    'explorer',
  );
  const [showAssignments, setShowAssignments] = useState(true);

  const handleAssignment = (assignment: Assignment) => {
    setShowAssignments(false);
    store.sendMessage(assignment.prompt);
  };

  const explorerOpen = activePanel === 'explorer' && store.explorerVisible;

  return (
    <div
      className="h-screen w-screen overflow-hidden bg-dark-bg text-gray-200 font-sans"
      style={{
        display: 'grid',
        gridTemplateColumns: explorerOpen
          ? '48px 260px 1fr'
          : '48px 1fr',
        gridTemplateRows: '1fr 22px',
      }}
    >
      {/* Activity Bar */}
      <div className="row-span-2">
        <ActivityBar
          activePanel={activePanel}
          onPanelChange={setActivePanel}
          onBack={store.backToProjectSelect}
        />
      </div>

      {/* File Explorer */}
      {explorerOpen && (
        <div className="row-span-1 overflow-hidden">
          <FileExplorer
            fileTree={store.fileTree}
            selectedPath={store.selectedPath}
            onSelectFile={(path) => store.openFile(path)}
            projectName={store.projectName}
          />
        </div>
      )}

      {/* Editor + Terminal */}
      <div className="row-span-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Editor */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <EditorPanel
            openTabs={store.openTabs}
            activeTab={store.activeTab}
            files={store.files}
            onCloseTab={(path) => store.closeTab(path)}
            onSelectTab={(path) => store.selectTab(path)}
          />
        </div>

        {/* Terminal */}
        <div className="h-[320px] min-h-[200px] overflow-hidden">
          <TerminalPanel
            messages={store.messages}
            isStreaming={store.isStreaming}
            messagesRemaining={store.messagesRemaining}
            onSendMessage={(msg) => {
              setShowAssignments(false);
              store.sendMessage(msg);
            }}
            bedrockAvailable={store.bedrockAvailable}
          />
        </div>

        {/* Assignment Cards Overlay */}
        <AssignmentCards
          onSelect={handleAssignment}
          visible={showAssignments && !store.hasStarted && store.bedrockAvailable}
        />
      </div>

      {/* Status Bar */}
      <div
        className="flex items-center justify-between px-4 text-[11px] font-mono bg-[#007acc] text-white"
        style={{ gridColumn: '2 / -1' }}
      >
        <div className="flex items-center gap-4">
          <span>CodeForge Demo</span>
          <span>{store.projectName}</span>
        </div>
        <div className="flex items-center gap-4">
          {store.hasStarted && !showAssignments && (
            <button
              onClick={() => setShowAssignments(true)}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity"
            >
              <ListTodo className="h-3 w-3" />
              Assignments
            </button>
          )}
          <span>
            Messages: {store.messagesRemaining} remaining
          </span>
          <button
            onClick={store.newSession}
            className="flex items-center gap-1 hover:opacity-80 transition-opacity"
          >
            <RotateCcw className="h-3 w-3" />
            New Session
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function TryItPage() {
  const store = useTryItStore();

  useEffect(() => {
    store.loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (store.screen === 'workspace' && store.sessionId) {
    return <WorkspaceScreen />;
  }

  return (
    <ProjectSelector
      projects={store.projects}
      isLoading={store.isLoading}
      error={store.error}
      onSelect={(id) => store.selectProject(id)}
      onRetry={() => store.loadProjects()}
    />
  );
}
